"use strict";

/**
 * @module mw-utils/Utils
 * @copyright Copyright 2011-2023 The MathWorks, Inc.
 */
define(['dojo/_base/lang', 'dojo/_base/window', 'mw-deprecation-utils/deprecation'], function (lang, dojoWindow, deprecation) {
  return {
    /**
     * @static
     * @description Generates a random string of 8 characters, where each character is a valid
     * hexadecimal digit (0-9, A-F). Alphabetic characters are capitalized. Note this does not
     * generate a standard UUID v4 string.
     * <br/>
     * For this 32-bit UUID, one would need to generate about 9300 UUIDs to have a 1% probability
     * of at least one collision.
     * <hr/>
     * <b>Note: This function makes use of Math.random() to generate the random UUIDs for simplicity
     * and performance. Please DO NOT use this UUID generator for any applications that require
     * cryptographic security.</b>
     * @returns {String} The new UUID string
     * @example
     * let uuid = Utils.generateUuid() // -> returns a string like 'A89B05F3'
     */
    generateUuid: function generateUuid() {
      const template = 'xxxxxxxx';
      return this._uuidFromTemplate(template).toUpperCase();
    },

    /**
     * @static
     * @description Generates a random, 36-character, standard UUID v4 string. This string
     * is of the form 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.
     * <br/>
     * For this 122-bit UUID (128 bit, but 4 bits reserved for the 4 and 2 bits reserved for
     * the "variant"), one would need to generate about 327 quadrillion (3.27 * 10^17) UUIDs
     * to have a 1% probability of at least one collision.
     * <hr/>
     * <b>Note: This function makes use of Math.random() to generate the random UUIDs for simplicity
     * and performance. Please DO NOT use this UUID generator for any applications that require
     * cryptographic security.</b>
     * @returns {String} The new UUID string
     * @example
     * let uuid = Utils.generateUUIDv4() // -> returns a string like 'e7546e1c-a919-4805-9a20-48ca0dbb48e5'
     */
    generateUUIDv4: function generateUUIDv4() {
      const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
      return this._uuidFromTemplate(template);
    },
    // Given a template string of x's and y's, generate a UUID by randomly generating hex
    // values to replace the x's and y's.
    _uuidFromTemplate: function _uuidFromTemplate(template) {
      return template.replace(/[xy]/g, function (c) {
        // Generate a random integer from 0-15
        const r = Math.random() * 16 | 0; // x's use the random integer, y's have their integer mutated: (int)%4+8

        const v = c === 'x' ? r : r & 0x3 | 0x8;
        return v.toString(16);
      });
    },

    /**
     * @static
     * @description Returns true if the given object is a dojo ImageData object or if it has
     * the expected width, height, and data properties, false otherwise.
     * @param {Object} obj - The object to test
     * @returns {Boolean}
     */
    isImageData: function isImageData(obj) {
      // Firefox does not have an ImageData class for data returned by getImageData
      return !!obj && (dojoWindow.global.ImageData && obj instanceof dojoWindow.global.ImageData || Object.prototype.hasOwnProperty.call(obj, 'width') && Object.prototype.hasOwnProperty.call(obj, 'height') && Object.prototype.hasOwnProperty.call(obj, 'data'));
    },

    /**
     * @static
     * @description Checks if an object is an array without making use of instanceof. instanceof
     * can yield unexpected results when used with multiple contexts, e.g. passing objects between
     * iframes.
     *
     * @deprecated Consider using <a href='https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/isArray'>Array.isArray()</a> instead.
     * @param {*} obj - The object to check
     * @returns {Boolean} True if obj is an Array, false otherwise
     */
    isArray: function isArray(obj) {
      deprecation.deprecated({
        deprecated: {
          module: 'mw-utils/Utils',
          method: 'isArray'
        },
        geckNumber: 1444368
      });
      return Array.isArray(obj);
    },

    /**
     * @static
     * @description Get the number of non-undefined array elements. If the provided object
     * is not an Array, returns false.
     * @param {Array} arr - The array whose elements we wish to count
     * @returns {Number | Boolean} If arr is an Array, returns the number of non-undefined
     * elements in arr. Returns false otherwise.
     */
    getArrayLength: function getArrayLength(arr) {
      let length = 0;

      if (!lang.isArray(arr)) {
        return false;
      }

      arr.forEach(function (item) {
        if (item) {
          length += 1;
        }
      });
      return length;
    },

    /**
     * @static
     * @description Returns true if the given text has only whitespace characters. LINE_FEED,
     * CARRIAGE_RETURN, TAB are treated as whitespace characters. Empty string is treated
     * as non whitespace.
     *
     * @param {String} text - A string of characters
     * @returns {Boolean}
     */
    isWhitespace: function isWhitespace(text) {
      const regex = /^\s+$/;
      return regex.test(text);
    },

    /**
     * @static
     * @description Removes leading and trailing newline characters from string.
     * @param {String} text - A string of characters
     * @returns {String} The input text without leading or trailing newline characters.
     */
    removeSurroundingNewLines: function removeSurroundingNewLines(text) {
      while (text.charAt(0) === '\n') {
        text = text.substring(1);
      }

      while (text.charAt(text.length - 1) === '\n') {
        text = text.substring(0, text.length - 1);
      }

      return text;
    },

    /**
     * @static
     * @description Finds the first element of arr2 that is not an element of arr1.
     *
     * @param {Array} array1 - Array to check elements against
     * @param {Array} array2 - Array to find unique element within
     * @returns {*} First unique element of array2, null if no unique elements
     */
    getFirstUniqueElement: function getFirstUniqueElement(array1, array2) {
      let unique = null;
      array2.some(function (elem) {
        if (array1.indexOf(elem) === -1) {
          unique = elem;
          return true;
        }

        return false;
      });
      return unique;
    },

    /**
     * @static
     * @description Finds the union of the two sets of string words (provided either as Arrays
     * or Strings separated by spaces) and returns a set of unique words
     *
     * @param {String | Array} set1 - The first set of strings, e.g. "word1" or "word1 word2"
     *      | ["word1"] or ["word1", "word2"]
     * @param {String | Array} set2 - The second set of strings, e.g. "word1" or "word1 word2"
     *      | ["word1"] or ["word1", "word2"]
     * @param {Boolean} [asString] - Set to true if the set union should be returned as a
     *      space-separated String. If set to false or unspecified, set union is returned as
     *      an Array.
     * @returns {Array | String} Unique set of words that were contained in either set
     */
    unionOfWordSets: function unionOfWordSets(set1, set2, asString) {
      set1 = this._stringToArray(set1);
      set2 = this._stringToArray(set2);
      const union = this.unionOfArrays(set1, set2);
      return asString ? union.join(' ') : union;
    },

    /**
     * @static
     * @description Finds the intersection of the two sets of string words (provided either
     * as Arrays or Strings separated by spaces) and returns a set of unique words
     *
     * @param {String | Array} set1 - The first set of strings, e.g. "word1" or "word1 word2"
     *      | ["word1"] or ["word1", "word2"]
     * @param {String | Array} set2 - The second set of strings, e.g. "word1" or "word1 word2"
     *      | ["word1"] or ["word1", "word2"]
     * @param {Boolean} [asString] - Set to true if the set intersection should be returned as a
     *      space-separated String. If set to false or unspecified, set intersection is returned as
     *      an Array.
     * @returns {Array | String} Unique set of words that were contained in both sets
     */
    intersectionOfWordSets: function intersectionOfWordSets(set1, set2, asString) {
      set1 = this._stringToArray(set1);
      set2 = this._stringToArray(set2);
      const intersection = this.intersectionOfArrays(set1, set2);
      return asString ? intersection.join(' ') : intersection;
    },

    /**
     * @static
     * @description Finds the difference of of the two sets of string words (the complement of set2
     * in set1, all elements that are in set1 that are not in set2). Returns a set of unique words
     *
     * @param {String | Array} set1 - The first set of strings, e.g. "word1" or "word1 word2"
     *      | ["word1"] or ["word1", "word2"]
     * @param {String | Array} set2 - The second set of strings, e.g. "word1" or "word1 word2"
     *      | ["word1"] or ["word1", "word2"]
     * @param {Boolean} [asString] - Set to true if the set difference should be returned as a
     *      space-separated String. If set to false or unspecified, set difference is returned as
     *      an Array.
     * @returns {Array | String} Unique set of words that were contained only in set1
     */
    differenceOfWordSets: function differenceOfWordSets(set1, set2, asString) {
      set1 = this._stringToArray(set1);
      set2 = this._stringToArray(set2);
      const difference = this.unique(this.differenceOfArrays(set1, set2));
      return asString ? difference.join(' ') : difference;
    },

    /**
     * @static
     * @description Finds the union of the two Arrays (elements that are in either array
     * and duplicates are only represented once)
     *
     * @param {Array} arr1 - The first array
     * @param {Array} arr2 - The second array
     * @returns {Array} The union of the two arrays
     */
    unionOfArrays: function unionOfArrays(arr1, arr2) {
      return this.unique(arr1.concat(arr2));
    },

    /**
     * @static
     * @description Finds the intersection of the two Arrays (elements that are contained
     * in both arrays and duplicates are only represented once)
     * @param {Array} arr1 - The first array
     * @param {Array} arr2 - The second array
     * @returns {Array} The intersection of the two arrays
     */
    intersectionOfArrays: function intersectionOfArrays(arr1, arr2) {
      return this.nonunique(arr1.concat(arr2));
    },

    /**
     * @static
     * @description Finds the difference of arr2 in arr1 (elements that are in arr1 that are
     * not in arr2)
     * @param {Array} arr1 - The first array
     * @param {Array} arr2 - The second array
     * @returns {Array} The difference of the two arrays
     */
    differenceOfArrays: function differenceOfArrays(arr1, arr2) {
      if (window.Map === undefined) {
        // g1512937 Not all clients have support for the Map API.
        // Fall back to a slower implementation.
        return this._legacyDifferenceOfArrays(arr1, arr2);
      }

      let isInBothArrays, elementInArray1, i;
      const mapOfArray2 = new Map();
      const result = []; // Convert arr2 into a map

      for (i = 0; i < arr2.length; i++) {
        mapOfArray2.set(arr2[i], null);
      } // Push elements in arr1, but not in arr2, into the result


      for (i = 0; i < arr1.length; i++) {
        elementInArray1 = arr1[i];
        isInBothArrays = mapOfArray2.has(elementInArray1);

        if (!isInBothArrays) {
          result.push(elementInArray1);
        }
      }

      return result;
    },

    /**
     * @static
     * @description Finds the difference of arr2 in arr1 (elements that are in arr1 that are
     * not in arr2). This is a slower implementation compared to
     * {@link module:mw-utils/Utils.differenceOfArrays|differenceOfArrays} that does not make use of
     * the Map API.
     * @param {Array} arr1 - The first array
     * @param {Array} arr2 - The second array
     * @returns {Array} The difference of the two arrays
     */
    _legacyDifferenceOfArrays: function _legacyDifferenceOfArrays(arr1, arr2) {
      return this.unique(arr1.filter(function (elem) {
        return arr2.indexOf(elem) === -1;
      }));
    },

    /**
     * @static
     * @description Returns a shallow copy of the given array with all duplicate elements
     * removed.
     * @param {Array} array
     * @returns {Array}
     */
    unique: function unique(array) {
      return array.filter(function (elem, idx) {
        return array.indexOf(elem) === idx;
      });
    },

    /**
     * @static
     * @description Returns a shallow copy of the given array with all unique elements removed.
     * Note that the resulting array will have duplicates removed. For example, nonunique([1, 2, 2, 3])
     * will return [2].
     * @param {Array} array
     * @returns {Array}
     */
    nonunique: function nonunique(array) {
      return this.unique(array.filter(function (elem, idx) {
        return array.indexOf(elem) !== idx;
      }));
    },

    /**
     * @static
     * @description Performs element-wise equality check on two arrays. Will always be true when ===
     * is true. Can also return true even when === returns false. Default element comparator is === .
     * @param {Array} array1 - The first array
     * @param {Array} array2 - The second array
     * @param {Function} [compareFunction] Optional comparison function that accepts two elements,
     *                                     and returns true if the elements are "equal", false if not.
     * @returns {Boolean}
     */
    arrayEquals: function arrayEquals(array1, array2, compareFunction) {
      if (array1 === array2) {
        return true;
      }

      if (array1.length !== array2.length) {
        return false;
      }

      if (!compareFunction) {
        compareFunction = function compareFunction(elem1, elem2) {
          return elem1 === elem2;
        };
      }

      for (let i = 0, len = array1.length; i < len; i++) {
        if (!compareFunction(array1[i], array2[i])) {
          return false;
        }
      } // if we get here, all elements were the same


      return true;
    },

    /**
     * @static
     * @description Returns a shallow copy of the given array with all "empty" elements
     * removed. "Empty" elements are empty strings and null or undefined values.
     * @param {Array} array
     * @returns {Array}
     */
    removeEmptyElements: function removeEmptyElements(array) {
      return array.filter(function (elem) {
        return !(elem === '' || elem === null || elem === undefined);
      });
    },
    _stringToArray: function _stringToArray(str) {
      if (!this._isString(str)) {
        return str;
      }

      return this.removeEmptyElements(str.split(' '));
    },
    _isString: function _isString(str) {
      return typeof str === 'string' || str instanceof String;
    }
  };
});
//# sourceMappingURL=Utils.js.map
